import { useMemo, useState, type ReactNode } from 'react';
import styles from './ceo/CeoPages.module.css';
import type { Unavailable } from './useReport';

export const inr = (v: unknown): string => {
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return '—';
  if (Math.abs(n) >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (Math.abs(n) >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
};
export const pct = (v: unknown, dp = 1): string => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? `${n.toFixed(dp)}%` : '—';
};
export const int = (v: unknown): string => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? Math.round(n).toLocaleString('en-IN') : '—';
};
/**
 * Safe display of an unknown cell.
 *
 * `String(v)` on an object prints "[object Object]" into the table, which
 * looks like data. Anything that is not already a primitive is rendered as an
 * em-dash instead.
 */
export const text = (v: unknown): string => {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') return v || '—';
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return '—';
};

export const dec = (v: unknown, dp = 2): string => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n.toFixed(dp) : '—';
};

/**
 * Sorting and filtering for a table that renders its own cells.
 *
 * The generic `Table` below covers plain rows; a table with a badge, a
 * sub-line or a coloured delta needs its own markup and still needs to sort.
 * The ordering rules live here so both behave identically: three-state cycle
 * (desc, asc, back to the API's own order — these lists arrive ranked), and
 * unknown values sort last in BOTH directions because a null is not a small
 * number.
 */
export function useSortFilter<T>(
  rows: T[],
  accessors: Record<string, (row: T) => string | number | null | undefined>,
  searchText?: (row: T) => string,
): {
  view: T[];
  sort: { key: string; dir: 'asc' | 'desc' } | null;
  toggle: (key: string) => void;
  query: string;
  setQuery: (q: string) => void;
} {
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(null);
  const [query, setQuery] = useState('');

  const view = useMemo(() => {
    const q = query.trim().toLowerCase();
    let out = q && searchText ? rows.filter((r) => searchText(r).toLowerCase().includes(q)) : rows;
    const get = sort ? accessors[sort.key] : undefined;
    if (sort && get) {
      out = [...out].sort((a, b) => {
        const av = get(a) ?? null;
        const bv = get(b) ?? null;
        if (av === null && bv === null) return 0;
        if (av === null) return 1;
        if (bv === null) return -1;
        const cmp = typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
        return sort.dir === 'asc' ? cmp : -cmp;
      });
    }
    return out;
  }, [rows, sort, query, accessors, searchText]);

  const toggle = (key: string): void => {
    setSort((prev) => {
      if (prev?.key !== key) return { key, dir: 'desc' };
      if (prev.dir === 'desc') return { key, dir: 'asc' };
      return null;
    });
  };

  return { view, sort, toggle, query, setQuery };
}

/** Header cell for a table using useSortFilter. */
export function SortHead({
  label, sortKey, sort, toggle, num, className,
}: {
  label: string;
  sortKey: string;
  sort: { key: string; dir: 'asc' | 'desc' } | null;
  toggle: (key: string) => void;
  num?: boolean;
  className?: string;
}) {
  const on = sort?.key === sortKey;
  return (
    <th
      className={`${num ? styles.num : ''} ${styles.sortable} ${on ? styles.sorted : ''} ${className ?? ''}`}
      aria-sort={on ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      onClick={() => { toggle(sortKey); }}
    >
      {label}
      {on && <span className={styles.caret} aria-hidden="true">{sort.dir === 'asc' ? '▲' : '▼'}</span>}
    </th>
  );
}

/** Search box for a table using useSortFilter. */
export function TableSearch({ query, setQuery, shown, total }: {
  query: string; setQuery: (q: string) => void; shown: number; total: number;
}) {
  return (
    <div className={styles.tools}>
      <input
        className={styles.search}
        value={query}
        placeholder="Search…"
        aria-label="Search table"
        onChange={(e) => { setQuery(e.target.value); }}
      />
      <span className={styles.count}>{query ? `${shown} of ${total}` : `${total} rows`}</span>
    </div>
  );
}

export function Card({ title, note, children }: { title: string; note?: string; children: ReactNode }) {
  return (
    <section className={styles.card}>
      <h3 className={styles.cardTitle}>{title}{note && <span className={styles.cardNote}>{note}</span>}</h3>
      {children}
    </section>
  );
}

export function Kpi({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: 'good' | 'bad' }) {
  return (
    <div className={styles.kpi}>
      <span className={styles.kpiLabel}>{label}</span>
      <span className={`${styles.kpiValue} ${tone === 'good' ? styles.good : ''}`}>{value}</span>
      {sub && <span className={styles.kpiSub}>{sub}</span>}
    </div>
  );
}

export function Kpis({ children }: { children: ReactNode }) {
  return <div className={styles.kpis}>{children}</div>;
}

/**
 * A panel the published report has and this one cannot compute.
 *
 * Named and explained rather than quietly dropped. A reader who knows the
 * report will look for it, and "it is not here because the data does not
 * exist" is a useful answer; an unexplained absence is not.
 */
export function NotMeasurable({ items }: { items: Unavailable[] }) {
  if (items.length === 0) return null;
  return (
    <section className={styles.card}>
      <h3 className={styles.cardTitle}>Not shown — and why</h3>
      <ul className={styles.findings}>
        {items.map((u) => (
          <li key={u.panel} className={styles.finding}>
            <p className={styles.findingText}><b>{u.panel}</b></p>
            <p className={styles.findingBasis}>{u.reason}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function Table({ columns, rows, render, searchable = true }: {
  columns: Array<{ key: string; label: string; num?: boolean }>;
  rows: Array<Record<string, unknown>>;
  render: (row: Record<string, unknown>, key: string) => ReactNode;
  searchable?: boolean;
}) {
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(null);
  const [query, setQuery] = useState('');

  const view = useMemo(() => {
    const q = query.trim().toLowerCase();
    let out = q
      ? rows.filter((r) =>
          Object.values(r)
            .filter((v): v is string | number => typeof v === 'string' || typeof v === 'number')
            .join(' ')
            .toLowerCase()
            .includes(q),
        )
      : rows;
    if (sort) {
      // Sorted on the RAW field, not the rendered string: "₹13.79 L" would
      // order before "₹2 Cr" as text.
      out = [...out].sort((a, b) => {
        const av = a[sort.key], bv = b[sort.key];
        const an = typeof av === 'number' ? av : typeof av === 'string' ? av : null;
        const bn = typeof bv === 'number' ? bv : typeof bv === 'string' ? bv : null;
        // Unknown sorts last either way — a null is not a small number.
        if (an === null && bn === null) return 0;
        if (an === null) return 1;
        if (bn === null) return -1;
        const cmp = typeof an === 'number' && typeof bn === 'number'
          ? an - bn
          : String(an).localeCompare(String(bn), undefined, { numeric: true, sensitivity: 'base' });
        return sort.dir === 'asc' ? cmp : -cmp;
      });
    }
    return out;
  }, [rows, sort, query]);

  const toggle = (key: string): void => {
    setSort((prev) => {
      if (prev?.key !== key) return { key, dir: 'desc' };
      if (prev.dir === 'desc') return { key, dir: 'asc' };
      // Third click restores the order the API returned, which is itself
      // meaningful — these lists arrive ranked.
      return null;
    });
  };

  return (
    <div className={styles.tableWrap}>
      {searchable && (
        <div className={styles.tools}>
          <input
            className={styles.search}
            value={query}
            placeholder="Search…"
            aria-label="Search table"
            onChange={(e) => { setQuery(e.target.value); }}
          />
          <span className={styles.count}>{query ? `${view.length} of ${rows.length}` : `${rows.length} rows`}</span>
        </div>
      )}
      <table className={styles.table}>
        <thead>
          <tr>{columns.map((c) => (
            <th
              key={c.key}
              className={`${c.num ? styles.num : ''} ${styles.sortable} ${sort?.key === c.key ? styles.sorted : ''}`}
              aria-sort={sort?.key === c.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
              onClick={() => { toggle(c.key); }}
            >
              {c.label}
              {sort?.key === c.key && <span className={styles.caret} aria-hidden="true">{sort.dir === 'asc' ? '▲' : '▼'}</span>}
            </th>
          ))}</tr>
        </thead>
        <tbody>
          {/* Identity fields alone are not unique — fill rate repeats a product
              across facilities and cities — so the row index is part of the key
              rather than a fallback for when identity is missing. */}
          {view.map((r, i) => (
            <tr key={`${[r['id'], r['product_id'], r['keyword'], r['city_name'], r['sub_line'], r['facility'], r['campaign']]
              .map(text).filter((x) => x !== '—').join('|')}#${i}`}>
              {columns.map((c) => (
                <td key={c.key} className={c.num ? styles.num : ''}>{render(r, c.key)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function State({ children }: { children: ReactNode }) {
  return <p className={styles.state}>{children}</p>;
}
export function ErrorBox({ children }: { children: ReactNode }) {
  return <p className={styles.error}>{children}</p>;
}
